import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ContextMenuItem {
  label: string;
  onClick?: () => void;
  /** Visual hint for destructive actions (red text). */
  danger?: boolean;
  /** Renders as a divider; ignores label/onClick. */
  separator?: boolean;
  /** Disables the row but leaves it visible (greyed out). */
  disabled?: boolean;
}

interface ContextMenuProps {
  /** Anchor point (clientX/Y from the triggering event). Null = closed. */
  position: { x: number; y: number } | null;
  items: ContextMenuItem[];
  onClose: () => void;
}

const ROW_H = 26;
const VPAD = 4;

export function ContextMenu({ position, items, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [adjusted, setAdjusted] = useState<{ x: number; y: number } | null>(null);

  // External system: dismiss on outside-click and Escape.
  useEffect(() => {
    if (!position) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [position, onClose]);

  // External system: nudge the menu back inside the viewport when the
  // anchor is near the edge. We measure after first render then patch
  // position state.
  useLayoutEffect(() => {
    if (!position || !ref.current) {
      setAdjusted(position);
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = position.x;
    let y = position.y;
    if (x + rect.width > vw - 4) x = Math.max(4, vw - rect.width - 4);
    if (y + rect.height > vh - 4) y = Math.max(4, vh - rect.height - 4);
    setAdjusted({ x, y });
  }, [position]);

  if (!position) return null;
  if (typeof document === 'undefined') return null;
  const { x, y } = adjusted ?? position;
  const visibleRows = items.filter((i) => !i.separator).length;
  const minHeight = visibleRows * ROW_H + VPAD * 2;

  // Portal to document.body so position:fixed actually escapes the
  // YesQL window's transformed ancestor (per CSS, position:fixed is
  // relative to the nearest transformed ancestor — which would be the
  // desktop Window — not the viewport).
  return createPortal(
    <div
      ref={ref}
      className="fixed z-50 min-w-[180px] rounded border border-[#4a4a4a] bg-[#3c3f41] py-1 shadow-2xl"
      style={{ left: x, top: y, minHeight }}
      role="menu"
    >
      {items.map((item, i) => {
        if (item.separator) {
          return <div key={`sep-${i}`} className="my-1 h-px bg-[#4a4a4a]" role="separator" />;
        }
        return (
          <button
            key={i}
            type="button"
            disabled={item.disabled}
            onClick={() => {
              item.onClick?.();
              onClose();
            }}
            className={
              'block w-full px-3 text-left text-[12px] ' +
              (item.disabled
                ? 'cursor-not-allowed text-[#606366]'
                : item.danger
                  ? 'text-[#ff8888] hover:bg-[#3a2424]'
                  : 'text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8]')
            }
            style={{ height: ROW_H }}
            role="menuitem"
          >
            {item.label}
          </button>
        );
      })}
    </div>,
    document.body,
  );
}
